import { Icon } from '@buggy/shared'
import { Button, Flex, Table } from 'antd'
import { isEmpty } from 'lodash-es'
import Link from 'next/link'
import { FunctionComponent, ReactNode } from 'react'
import styled from 'styled-components'
import { RankType } from '@/api/workspaces-api'
import Avatar from '@/components/billboard/top100/content/avatar'
import { NoData } from '@/components/kol/detail/no-data'
import useUrlControl from '@/components/public-page/hook/use-url-control'
import { Rwd } from '@/components/rwd'
import { useAuth } from '@/hooks/use-auth'
import { Page } from '@/hooks/use-authorization/constants'
import routes from '@/hooks/use-authorization/routes'
import { useI18n } from '@/hooks/use-i18n'
import { useIntl } from '@/i18n/hooks/use-intl'
import {
  AnalyzeRankingItem,
  RankingPlatformType,
} from '@/types/schema/k6/analyze-api'
import { ampli } from '@/utils/ampli'
import { isPlatformShortcode } from '@/utils/platform/is-platform-shortcode'

export interface BillboardProps {
  className?: string
  analyzeRankingList: AnalyzeRankingItem[]
  platform: RankingPlatformType
  rankType?: RankType
  lastUpdatedAt: string | null
  displayKolLink?: boolean
  displayTableLastUpdatedAt?: boolean
  from?: string
  registerSearchParams?: typeof routes.register.searchParamsInputType
  isFetching?: boolean
}

const Billboard: FunctionComponent<BillboardProps> = ({
  className,
  analyzeRankingList,
  platform,
  rankType = RankType.WordOfMouthVolume,
  lastUpdatedAt,
  displayKolLink,
  displayTableLastUpdatedAt,
  from = 'Landing Page',
  registerSearchParams,
  isFetching,
}) => {
  const { formatNumber, formatDate, formatMessage } = useIntl()
  const { getAppUrl } = useUrlControl()
  const { isAd } = useAuth()
  const { locale } = useI18n()

  const upperCasePlatform = platform.toUpperCase()
  const platformShortCode = isPlatformShortcode(platform) ? platform : undefined
  const getKolUrl = (uuid: string): string => {
    return routes.publicKolDetail.stringifyUrl(
      { kolId: uuid },
      { from, platform: platformShortCode, lang: locale },
    )
  }

  return (
    <Wrapper className={className}>
      <Rwd pc mobile={false}>
        {displayTableLastUpdatedAt && lastUpdatedAt && (
          <DateWrapper textAlign='right'>
            {formatMessage({ id: 'landing:updated_at' })}:
            {formatDate(lastUpdatedAt)}
          </DateWrapper>
        )}
        <Table
          dataSource={analyzeRankingList}
          loading={isFetching}
          locale={{
            emptyText: <NoData />,
          }}
          pagination={false}
          rowKey={(item): string => item.uuid}
          scroll={{ x: true }}
          tableLayout='auto'
        >
          <Table.Column
            dataIndex='rank'
            title={formatMessage({ id: 'landing:ranking' })}
          />
          <Table.Column<AnalyzeRankingItem>
            dataIndex='uuid'
            render={(_, item): ReactNode => {
              const { followerCount, name, uuid } = item
              return (
                <Card>
                  <Avatar
                    from='Landing Page'
                    height={60}
                    platform={platform}
                    uuid={uuid}
                    width={60}
                  />
                  <Info>
                    <Name>
                      <Link href={getKolUrl(uuid)}>{name}</Link>
                    </Name>
                    <Desc>
                      {followerCount === null
                        ? '-'
                        : `${formatNumber(followerCount)} ${formatMessage({
                            id: 'project:metric_followers',
                          })}`}
                    </Desc>
                  </Info>
                </Card>
              )
            }}
            title={formatMessage({ id: 'general:social_media_accounts' })}
          />
          <Table.Column
            dataIndex='wordOfMouthVolume'
            render={(value): string =>
              value === null ? '-' : formatNumber(Math.ceil(value))
            }
            title={`${upperCasePlatform} ${formatMessage({
              id: 'general:buzz_volume',
            })}`}
          />
          <Table.Column
            dataIndex='engagementRate'
            render={(value): string =>
              value === null ? '-' : `${Math.ceil(value * 100)}%`
            }
            title={`${upperCasePlatform} ${formatMessage({
              id: 'project:metric_engagement_rate',
            })}`}
          />
          <Table.Column
            dataIndex='viewRate'
            render={(value): string =>
              value === null ? '-' : `${Math.ceil(value * 100)}%`
            }
            title={`${upperCasePlatform} ${formatMessage({
              id: 'project:metric_view_rate',
            })}`}
          />
          {displayKolLink && (
            <Table.Column
              dataIndex='uuid'
              render={(uuid): ReactNode => {
                return (
                  <KolLinkButton
                    danger
                    href={getKolUrl(uuid)}
                    icon={<Icon name='radar-chart-outlined' />}
                  >
                    {formatMessage({ id: 'landing:analyze_the_influencer' })}
                  </KolLinkButton>
                )
              }}
              title={formatMessage({ id: 'landing:kol_detail' })}
            />
          )}
        </Table>
      </Rwd>
      <Rwd mobile pc={false}>
        {lastUpdatedAt && (
          <DateWrapper>
            {formatMessage({ id: 'landing:updated_at' })}:
            {formatDate(lastUpdatedAt)}
          </DateWrapper>
        )}
        <MobileList>
          {isEmpty(analyzeRankingList) ? (
            <NoData />
          ) : (
            analyzeRankingList.map((item) => {
              const {
                followerCount,
                name,
                uuid,
                wordOfMouthVolume,
                engagementRate,
                viewRate,
              } = item
              const kolUrl = routes.publicKolDetail.stringifyUrl(
                { kolId: uuid },
                { from, platform: platformShortCode, lang: locale },
              )

              return (
                <MobileRow key={item.uuid}>
                  <MobileRank>{item.rank}</MobileRank>
                  <Avatar
                    from={from ?? 'Landing Page'}
                    height={60}
                    platform={platform}
                    uuid={uuid}
                    width={60}
                  />
                  <Info>
                    <Name>
                      <Link href={kolUrl}>{name}</Link>
                    </Name>
                    <Desc>
                      {followerCount === null
                        ? '-'
                        : `${formatNumber(followerCount)} ${formatMessage({
                            id: 'project:metric_followers',
                          })}`}
                    </Desc>
                    <AdditionalInfo>
                      <Desc>
                        {`${upperCasePlatform} ${formatMessage({
                          id: 'general:buzz_volume',
                        })}`}
                        ：
                        {wordOfMouthVolume === null
                          ? '-'
                          : `${formatNumber(Math.ceil(wordOfMouthVolume))}`}
                      </Desc>
                      <Desc>
                        {upperCasePlatform}
                        {formatMessage({
                          id: 'project:metric_engagement_rate',
                        })}
                        ：
                        {engagementRate === null
                          ? '-'
                          : `${Math.ceil(engagementRate * 100)}%`}
                      </Desc>
                      <Desc>
                        {`${upperCasePlatform} ${formatMessage({
                          id: 'project:metric_view_rate',
                        })}`}
                        ：
                        {viewRate === null
                          ? '-'
                          : `${Math.ceil(viewRate * 100)}%`}
                      </Desc>
                    </AdditionalInfo>
                    {displayKolLink && (
                      <KolLinkButton
                        danger
                        href={kolUrl}
                        icon={<Icon name='radar-chart-outlined' />}
                      >
                        {formatMessage({
                          id: 'landing:analyze_the_influencer',
                        })}
                      </KolLinkButton>
                    )}
                  </Info>
                </MobileRow>
              )
            })
          )}
        </MobileList>
      </Rwd>
      <Flex justify='center'>
        {isAd ? (
          <Link
            href={getAppUrl(Page.InfluencersRanking, {
              platform,
              countryCode: 'tw',
              rankType,
            })}
          >
            <Button
              icon={<Icon fontSize={16} name='chevrons-down' />}
              type='primary'
            >
              {formatMessage({ id: 'landing:view_full_ranking_list' })}
            </Button>
          </Link>
        ) : (
          <Link
            href={getAppUrl(Page.Register, {
              from: registerSearchParams?.from ?? 'home',
              ...registerSearchParams,
            })}
          >
            <Button
              icon={<Icon fontSize={16} name='chevrons-down' />}
              type='primary'
              onClick={(): void => {
                ampli.clicksRegister({
                  from,
                })
              }}
            >
              {formatMessage({
                id: 'landing:register_and_view_full_ranking_list',
              })}
            </Button>
          </Link>
        )}
      </Flex>
    </Wrapper>
  )
}

const KolLinkButton = styled(Button)`
  display: flex;
  align-items: center;
  width: fit-content;

  @media (max-width: 768px) {
    margin-top: 16px;
  }
`

const Name = styled.div`
  font-size: 16px;
  font-weight: 700;

  a {
    color: ${({ theme }): string => theme.colors.text.primary};

    &:hover {
      ${({ theme }): string => theme.colors.text.primary};
    }
  }
`
const Desc = styled.div`
  font-size: 14px;
`
const Info = styled.div`
  color: ${({ theme }): string => theme.colors.text.primary};

  ${Name} {
    margin-bottom: 4px;
  }
`

const Card = styled.div`
  display: flex;
  align-items: center;
  line-height: 1.4;

  ${Avatar} {
    margin-right: 16px;
  }
`

const DateWrapper = styled.div<{
  textAlign?: 'center' | 'left' | 'right'
}>`
  font-size: 14px;
  font-weight: 400;
  line-height: 1.4;
  text-align: ${({ textAlign }): string => textAlign ?? 'center'};
`

const MobileRank = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }): string => theme.colors.text.secondary};
  height: 60px;
  width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const AdditionalInfo = styled.div`
  ${Desc} {
    line-height: 1.6;
    margin-bottom: 8px;

    &:last-child {
      margin-bottom: 0;
    }
  }
`

const MobileRow = styled.div`
  display: flex;
  padding: 16px 0;
  border-bottom: solid 1px
    ${({ theme }): string => theme.colors.base.grey.grey6};

  &:first-child {
    border-top: solid 1px ${({ theme }): string => theme.colors.base.grey.grey6};
  }

  ${MobileRank} {
    margin-right: 16px;
  }

  ${Avatar} {
    margin-right: 16px;
  }

  ${AdditionalInfo} {
    margin-top: 16px;
  }
`

const MobileList = styled.div``

const Wrapper = styled.div`
  width: 100%;
  padding: 40px;
  background-color: ${({ theme }): string => theme.colors.background.white};
  box-shadow: 0px 2px 12px 0px rgb(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    padding: 24px 12px;
  }

  .ant-table-wrapper {
    margin-bottom: 40px;
  }

  ${MobileList} {
    margin-bottom: 40px;
  }

  .ant-table-thead > tr > th {
    background: ${({ theme }): string => theme.colors.background.white};
  }

  ${DateWrapper} {
    margin-bottom: 24px;
  }
`

export default styled(Billboard)``
